/**
 * pageContext.ts — 현재 화면에서 사용자가 작성 중인 문서의 상태를 수집해
 * 플로팅 챗봇이 "추가로 선택하면 좋은 항목" 등을 제안할 수 있도록 텍스트로 만든다.
 *
 * 수집 대상 (#main-content 내부, 챗봇 위젯 제외):
 *   - 텍스트/textarea: 라벨 + 입력값
 *   - select: 라벨 + 현재 선택 + 선택 가능한 전체 옵션 목록
 *   - 체크박스/라디오 그룹: 그룹 라벨 + [선택됨] + [선택 가능(미선택)] 목록
 * → 챗봇이 "선택됨"과 "선택 가능" 을 모두 보고 안전상 추가 선택을 제안하도록.
 */

// 위젯의 시스템 노트(지침 ~700자) + 컨텍스트가 백엔드 per-turn 한도(3000자) 안에 들도록.
const MAX_CTX_CHARS = 2200
const MAX_FIELDS = 60

function cleanText(s: string | null | undefined): string {
  return (s ?? '').replace(/\s+/g, ' ').trim()
}

function labelForControl(el: HTMLElement): string {
  const aria = el.getAttribute('aria-label')
  if (aria && aria.trim()) return cleanText(aria)
  const id = (el as HTMLInputElement).id
  if (id) {
    const lbl = document.querySelector(`label[for="${CSS.escape(id)}"]`)
    const t = cleanText(lbl?.textContent)
    if (t) return t
  }
  const wrapLbl = el.closest('label')
  if (wrapLbl) {
    const t = cleanText(wrapLbl.textContent).replace(cleanText((el as HTMLInputElement).value), '').trim()
    if (t && t.length <= 60) return t
  }
  let prev: Element | null = el.previousElementSibling
  for (let i = 0; i < 3 && prev; i++) {
    const t = cleanText(prev.textContent)
    if (t && t.length <= 50) return t
    prev = prev.previousElementSibling
  }
  const ph = (el as HTMLInputElement).placeholder
  if (ph && ph.trim()) return cleanText(ph).slice(0, 40)
  return ''
}

/** 체크박스/라디오의 표시 라벨 (감싸는 label, 인접 텍스트, value 순). */
function checkboxLabel(el: HTMLInputElement): string {
  const wrapLbl = el.closest('label')
  if (wrapLbl) {
    const t = cleanText(wrapLbl.textContent)
    if (t && t.length <= 60) return t
  }
  // 인접한 span/label
  let sib: Element | null = el.nextElementSibling
  for (let i = 0; i < 3 && sib; i++) {
    const t = cleanText(sib.textContent)
    if (t && t.length <= 60) return t
    sib = sib.nextElementSibling
  }
  // 부모 컨테이너 텍스트 (체크박스 1개만 든 작은 컨테이너)
  const parent = el.parentElement
  if (parent) {
    const t = cleanText(parent.textContent)
    if (t && t.length <= 60) return t
  }
  return cleanText(el.value) || el.getAttribute('aria-label') || ''
}

/** 체크박스/라디오 그룹의 헤더 라벨 — 그룹 컨테이너 위쪽의 제목성 텍스트. */
function groupLabel(container: Element): string {
  // 컨테이너 내부 첫 헤더/라벨성 요소
  const head = container.querySelector('h2, h3, h4, h5, legend, label, p, .text-xs, .text-sm')
  const ht = cleanText(head?.textContent)
  if (ht && ht.length <= 50) return ht
  // 컨테이너 직전 형제
  let prev: Element | null = container.previousElementSibling
  for (let i = 0; i < 3 && prev; i++) {
    const t = cleanText(prev.textContent)
    if (t && t.length <= 50) return t
    prev = prev.previousElementSibling
  }
  return '항목 그룹'
}

function isVisible(el: HTMLElement): boolean {
  if (el.offsetParent !== null) return true
  // select 는 종종 offsetParent null 이지만 보일 수 있음
  const r = el.getBoundingClientRect()
  return r.width > 0 && r.height > 0
}

export function collectPageContext(): string {
  try {
    const root = document.getElementById('main-content') ?? document.body
    const lines: string[] = []
    const seen = new Set<string>()
    const push = (line: string) => {
      const l = line.slice(0, 220)
      if (!seen.has(l)) { seen.add(l); lines.push(l) }
    }

    // ── 1) 체크박스/라디오 — 컨테이너 단위로 그룹화 ──
    const checks = Array.from(root.querySelectorAll<HTMLInputElement>('input[type="checkbox"], input[type="radio"]'))
      .filter((el) => !el.closest('[data-floating-chat]') && isVisible(el))
    const groups = new Map<Element, HTMLInputElement[]>()
    for (const el of checks) {
      // 가장 가까운 "그룹 같은" 조상: 여러 체크박스를 담는 컨테이너 추정
      let c: Element | null = el.parentElement
      // 위로 올라가며 같은 type 의 input 을 2개 이상 가진 첫 컨테이너 선택 (최대 5단계)
      let groupContainer: Element = el.parentElement ?? root
      for (let i = 0; i < 5 && c; i++) {
        const cnt = c.querySelectorAll(`input[type="${el.type}"]`).length
        if (cnt >= 2) { groupContainer = c; break }
        if (c === root) break
        c = c.parentElement
      }
      const arr = groups.get(groupContainer) ?? []
      arr.push(el); groups.set(groupContainer, arr)
    }
    for (const [container, els] of groups) {
      if (lines.length >= MAX_FIELDS) break
      const selected: string[] = []
      const available: string[] = []
      for (const el of els) {
        const lab = checkboxLabel(el)
        if (!lab) continue
        if (el.checked) selected.push(lab); else available.push(lab)
      }
      // 단일 체크박스(토글)면 그룹 표기 생략하고 값만
      if (els.length === 1) {
        const lab = checkboxLabel(els[0])
        if (lab) push(`- ${lab}: ${els[0].checked ? '예' : '아니오'}`)
        continue
      }
      const gl = groupLabel(container)
      const selStr = selected.length ? selected.join(', ') : '(없음)'
      const avStr = available.length ? available.slice(0, 30).join(', ') : '(없음)'
      push(`[${gl}] 선택됨: ${selStr} / 선택 가능(미선택): ${avStr}`)
    }

    // ── 2) select — 현재값 + 옵션 목록 ──
    for (const el of Array.from(root.querySelectorAll<HTMLSelectElement>('select'))) {
      if (lines.length >= MAX_FIELDS) break
      if (el.closest('[data-floating-chat]') || !isVisible(el)) continue
      const label = labelForControl(el) || '(선택)'
      const cur = el.options[el.selectedIndex]?.text?.trim() || el.value || '(미선택)'
      const opts = Array.from(el.options).map((o) => cleanText(o.text)).filter(Boolean).slice(0, 25)
      push(`[${label}] 현재: ${cur} / 가능한 옵션: ${opts.join(', ')}`)
    }

    // ── 3) text/textarea/number/date — 입력값 ──
    for (const el of Array.from(root.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input, textarea'))) {
      if (lines.length >= MAX_FIELDS) break
      if (el.closest('[data-floating-chat]') || !isVisible(el)) continue
      const type = ((el as HTMLInputElement).type || '').toLowerCase()
      if (['password', 'hidden', 'file', 'search', 'checkbox', 'radio', 'submit', 'button'].includes(type)) continue
      const val = cleanText((el as HTMLInputElement).value)
      if (!val) continue
      const label = labelForControl(el) || '(항목)'
      push(`- ${label}: ${val}`)
    }

    if (lines.length === 0) return ''
    const screen = document.title?.replace(/\s*[—|·-].*$/, '').trim() || location.pathname
    let ctx = `[현재 화면: ${screen}]\n${lines.join('\n')}`
    if (ctx.length > MAX_CTX_CHARS) ctx = ctx.slice(0, MAX_CTX_CHARS) + '\n…(이하 생략)'
    return ctx
  } catch {
    return ''
  }
}
